import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Card,
  Modal,
  ModalHeader,
  ModalBody,
  Badge,
  CardHeader,
  Button,
  CardBody,
  Input,
} from "reactstrap";
import IntlMessages from "../../helpers/IntlMessages";
import { Separator } from "../../components/common/CustomBootstrap";
import {
  AddCommaNumber,
  buildPrintPageStyle,
  getStoredPrintPaperWidthMm,
} from "../../helpers/Utils";
import PerfectScrollbar from "react-perfect-scrollbar";
import ProductImage from "../../components/common/ProductImage";
import ReactToPrint from "react-to-print";
import ComponentToPrintNow from "../../containers/pages/ComponentToPrintNow";
import { getProvisionalInvoicePrintCode } from "../../helpers/ProvisionalInvoicePrint";
import { getFeeOtherLabelMessageId } from "../../helpers/FeeOtherLabel";

const getWidth = () =>
  window.innerWidth ||
  document.documentElement.clientWidth ||
  document.body.clientWidth;

function useCurrentWidth() {
  let [width, setWidth] = useState(getWidth());
  useEffect(() => {
    let timeoutId = null;
    const resizeListener = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => setWidth(getWidth()), 150);
    };
    window.addEventListener("resize", resizeListener);
    return () => window.removeEventListener("resize", resizeListener);
  }, []);
  return width;
}

const ProvisionalInvoice = ({
  isOpen,
  toggle,
  dataProvisionalInvoice,
  reOrders,
  onCancelProvisionalInvoice,
  onQuickPayProvisional, // (orderIds: number[]) => Promise<void>
  switchCheckedBill,
  print_customer_invoice,
  companyName,
  companyAddress,
  companyLogo,
  companyWebsite,
}) => {
  useCurrentWidth();

  const componentRef = useRef(null);
  const [dataToPrint, setDataToPrint] = useState(null);

  // Checked bill_ids (for bulk)
  const [selectedBillIds, setSelectedBillIds] = useState([]);
  const [isPaying, setIsPaying] = useState(false);

  // Reset selection whenever modal opens/closes or data changes
  useEffect(() => {
    setSelectedBillIds([]);
  }, [isOpen, dataProvisionalInvoice]);

  // Sort newest first
  const sortedData = dataProvisionalInvoice
    ? [...dataProvisionalInvoice].sort((a, b) => {
        const getLatest = (d) => {
          const orders = d?.orders || [];
          if (!orders.length) return "";
          const last = orders[orders.length - 1];
          return `${last?.date || ""} ${last?.time || ""}`;
        };
        return getLatest(b).localeCompare(getLatest(a));
      })
    : [];

  const calcSubtotal = (orders) =>
    (orders || []).reduce(
      (sum, o) => sum + parseFloat(o.price || 0) * (o.count || 1),
      0,
    );

  const formatAdjustmentValue = (adj) => {
    if (!adj?.value) return "";
    if (adj.type === "percent") {
      return adj.value;
    }
    const cleaned = String(adj.value).replace(/\./g, "");
    return AddCommaNumber(parseFloat(cleaned) || 0);
  };

  const calcTotal = (orders) => {
    const sub = calcSubtotal(orders);
    const first = orders?.[0] || {};
    let total = sub;
    const apply = (adj, sign) => {
      if (!adj?.value) return;
      const cleaned = String(adj.value).replace(/\./g, "");
      const val = parseFloat(cleaned);
      total += adj.type === "percent" ? (sign * (sub * val)) / 100 : sign * val;
    };
    apply(first.discount, -1);
    apply(first.tax_fee, +1);
    apply(first.fee_other, +1);
    return Math.max(0, total);
  };

  // ── Helpers ──────────────────────────────────────────────────────────────
  const allBillIds = sortedData.map((d) => d.bill_id).filter(Boolean);
  const isAllSelected =
    allBillIds.length > 0 &&
    allBillIds.every((id) => selectedBillIds.includes(id));

  const toggleBill = (billId) => {
    setSelectedBillIds((prev) =>
      prev.includes(billId)
        ? prev.filter((id) => id !== billId)
        : [...prev, billId],
    );
  };

  const toggleAll = () => {
    setSelectedBillIds(isAllSelected ? [] : [...allBillIds]);
  };

  // Collect all order IDs belonging to a set of bill_ids
  const getOrderIds = (billIds) =>
    sortedData
      .filter((d) => billIds.includes(d.bill_id))
      .flatMap((d) => d.orders.flatMap((o) => o.ids || [o.id]));

  const handleQuickPay = useCallback(
    async (billId) => {
      if (!onQuickPayProvisional) return;
      setIsPaying(true);
      try {
        await onQuickPayProvisional(getOrderIds([billId]));
      } finally {
        setIsPaying(false);
      }
    },
    [onQuickPayProvisional, sortedData],
  );

  const handleBulkPay = async () => {
    if (!onQuickPayProvisional || selectedBillIds.length === 0) return;
    setIsPaying(true);
    try {
      await onQuickPayProvisional(getOrderIds(selectedBillIds));
      setSelectedBillIds([]);
    } finally {
      setIsPaying(false);
    }
  };

  const handleBeforeGetContent = useCallback(() => {
    return new Promise((resolve) => {
      setTimeout(resolve, 300);
    }).then(async () => {
      if (
        componentRef.current &&
        typeof componentRef.current.prepareForPrint === "function"
      ) {
        await componentRef.current.prepareForPrint();
      }
    });
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      wrapClassName="modal-right modal-right-order-history"
      backdrop={true}
    >
      <ModalHeader toggle={toggle}>
        <IntlMessages id="home.provisional.title" />
      </ModalHeader>
      <ModalBody>
        <div style={{ display: "none" }}>
          <ComponentToPrintNow
            ref={componentRef}
            items={dataToPrint?.orders || []}
            companyName={companyName}
            companyAddress={companyAddress}
            companyLogo={companyLogo}
            companyWebsite={companyWebsite}
            billDate={dataToPrint?.orders?.[0]?.date}
            discount={dataToPrint?.orders?.[0]?.discount}
            tax_fee={dataToPrint?.orders?.[0]?.tax_fee}
            fee_other={dataToPrint?.orders?.[0]?.fee_other}
            pin_print={getProvisionalInvoicePrintCode(dataToPrint)}
            customer_name={dataToPrint?.orders?.[0]?.customer_name}
            customer_phone={dataToPrint?.orders?.[0]?.phone}
            print_customer={print_customer_invoice}
          />
        </div>

        {/* ── Bulk action bar ── */}
        {sortedData.length > 0 && (
          <div
            className="d-flex align-items-center justify-content-between mb-2 px-1 mx-3"
            style={{ gap: "8px" }}
          >
            {/* Select all */}
            <label
              className="d-flex align-items-center mb-0 cursor-pointer"
              style={{ gap: "6px", fontSize: "0.85rem", cursor: "pointer" }}
            >
              <Input
                type="checkbox"
                checked={isAllSelected}
                onChange={toggleAll}
                style={{ marginTop: 0 }}
              />
              Chọn tất cả ({sortedData.length})
            </label>

            {/* Pay selected button */}
            <Button
              color="success"
              size="sm"
              disabled={selectedBillIds.length === 0 || isPaying}
              onClick={handleBulkPay}
              className="px-3"
            >
              {isPaying
                ? "Đang xử lý..."
                : `Thanh toán đã chọn (${selectedBillIds.length})`}
            </Button>
          </div>
        )}

        <div className="scroll provisional-list">
          {sortedData && sortedData.length > 0 ? (
            <PerfectScrollbar
              options={{ suppressScrollX: true, wheelPropagation: true }}
            >
              {sortedData.map((data, index) => {
                const billId = data.bill_id;
                const isSelected = selectedBillIds.includes(billId);

                return (
                  <Card
                    className="mb-3 mt-3"
                    key={index}
                    style={{
                      border: isSelected ? "2px solid #28a745" : undefined,
                      transition: "border 0.15s",
                    }}
                  >
                    <CardHeader className="p-0 mx-2 d-flex justify-content-between align-items-center flex-wrap">
                      {/* Left: checkbox + info */}
                      <div
                        className="p-3 mx-2 mb-0 d-flex align-items-start"
                        style={{ gap: "10px" }}
                      >
                        {/* Checkbox */}
                        <Input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleBill(billId)}
                          style={{ marginTop: "4px", cursor: "pointer" }}
                        />
                        <div>
                          <h5 className="mb-0">
                            {data?.orders?.[0]?.table_id ? (
                              `Bàn ${data?.orders?.[0]?.table_id}`
                            ) : (
                              <IntlMessages id="home.provisional.counter" />
                            )}
                          </h5>
                          {(data?.orders?.[0]?.customer_name ||
                            data?.orders?.[0]?.phone) && (
                            <div
                              className="text-primary font-weight-bold mt-1"
                              style={{ fontSize: "0.85rem" }}
                            >
                              {[
                                data?.orders?.[0]?.customer_name,
                                data?.orders?.[0]?.phone,
                              ]
                                .filter(Boolean)
                                .join(" - ")}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Right: action buttons */}
                      <div
                        className="mr-2 mb-2 d-flex flex-wrap"
                        style={{ gap: "6px" }}
                      >
                        <Button
                          className="btn-sm px-3"
                          color="primary"
                          onClick={() => reOrders(data)}
                        >
                          <IntlMessages id="home.provisional.btn-reorder" />
                        </Button>

                        {switchCheckedBill ? (
                          <ReactToPrint
                            trigger={() => (
                              <Button
                                className="btn-sm px-3"
                                color="success"
                                disabled={isPaying}
                              >
                                Thanh toán
                              </Button>
                            )}
                            content={() => componentRef.current}
                            pageStyle={buildPrintPageStyle(
                              getStoredPrintPaperWidthMm("customer"),
                            )}
                            onBeforeGetContent={async () => {
                              setDataToPrint(data);
                              return handleBeforeGetContent();
                            }}
                            onAfterPrint={() => handleQuickPay(billId)}
                          />
                        ) : (
                          <Button
                            className="btn-sm px-3"
                            color="success"
                            disabled={isPaying}
                            onClick={() => handleQuickPay(billId)}
                          >
                            Thanh toán
                          </Button>
                        )}

                        <Button
                          className="btn-sm px-3"
                          color="danger"
                          onClick={() => onCancelProvisionalInvoice(data)}
                        >
                          <IntlMessages id="home.provisional.btn-cancel" />
                        </Button>
                      </div>
                    </CardHeader>

                    <Separator className="mb-3" />

                    {data?.orders?.map((order) => (
                      <div className="d-flex flex-row mb-3 px-3" key={order?.id}>
                        <div className="d-block position-relative mr-3">
                          <ProductImage
                            image={order.product.image}
                            alt={order.name}
                            fnbType={order.product.fnb_type}
                            className="list-thumbnail responsive border-0"
                            iconSize="40px"
                          />
                          {order?.count > 1 && (
                            <Badge
                              color="danger"
                              pill
                              className="position-absolute"
                              style={{ top: -5, right: -5, fontSize: "0.7rem" }}
                            >
                              x{order.count}
                            </Badge>
                          )}
                        </div>
                        <div className="card-body-custom d-flex flex-column min-width-zero p-05rem">
                          <div className="d-flex justify-content-between">
                            <span className="list-item-heading">
                              {order?.product?.name}
                              {order?.count > 1 ? ` (x${order.count})` : ""}
                            </span>
                            <span>
                              <Badge color="primary">
                                {AddCommaNumber(order?.price)} đ
                              </Badge>
                            </span>
                          </div>
                          <div
                            className="d-flex flex-wrap mt-1"
                            style={{ gap: "4px" }}
                          >
                            {order?.size ? (
                              <Badge
                                color="info"
                                className="mr-1"
                                style={{ fontSize: "0.65rem" }}
                              >
                                {order.size.split(",")[0]}
                              </Badge>
                            ) : null}
                            {order?.flavor ? (
                              <Badge
                                color="info"
                                className="mr-1"
                                style={{ fontSize: "0.65rem" }}
                              >
                                Vị: {order.flavor.split(",")[0]}
                              </Badge>
                            ) : null}
                            {order?.topping
                              ? order.topping
                                  .split("|")
                                  .filter((e) => e.trim() !== "")
                                  .map((e, i) => (
                                    <Badge
                                      color="success"
                                      className="mr-1"
                                      key={i}
                                      style={{ fontSize: "0.65rem" }}
                                    >
                                      {e.split(",")[0]?.trim()}
                                    </Badge>
                                  ))
                              : null}
                            {order?.ice && order.ice !== "100" ? (
                              <Badge
                                color="warning"
                                className="mr-1"
                                style={{ fontSize: "0.65rem" }}
                              >
                                Lượng đá: {order.ice}%
                              </Badge>
                            ) : null}
                            {order?.sugar && order.sugar !== "100" ? (
                              <Badge
                                color="secondary"
                                className="mr-1"
                                style={{ fontSize: "0.65rem" }}
                              >
                                Độ ngọt: {order.sugar}%
                              </Badge>
                            ) : null}
                            {order?.note ? (
                              <Badge
                                color="dark"
                                className="mr-1"
                                style={{ fontSize: "0.65rem" }}
                              >
                                📝 {order.note}
                              </Badge>
                            ) : null}
                          </div>
                          <div className="text-primary text-small font-weight-medium d-sm-block mt-1">
                            {order?.date} - {order?.time}
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Totals */}
                    <div className="px-3 pb-3">
                      <Separator className="mb-2" />
                      {(data?.orders?.[0]?.discount?.value ||
                        data?.orders?.[0]?.tax_fee?.value ||
                        data?.orders?.[0]?.fee_other?.value) && (
                        <div
                          className="d-flex flex-wrap mb-2"
                          style={{ gap: "6px" }}
                        >
                          {data.orders[0].discount?.value ? (
                            <Badge
                              color="warning"
                              style={{ fontSize: "0.7rem" }}
                            >
                              <IntlMessages id="home.summary.discount" />:{" "}
                              {formatAdjustmentValue(data.orders[0].discount)}
                              {data.orders[0].discount.type === "percent"
                                ? "%"
                                : " đ"}
                            </Badge>
                          ) : null}
                          {data.orders[0].tax_fee?.value ? (
                            <Badge
                              color="danger"
                              style={{ fontSize: "0.7rem" }}
                            >
                              <IntlMessages id="home.summary.tax" />:{" "}
                              {formatAdjustmentValue(data.orders[0].tax_fee)}
                              {data.orders[0].tax_fee.type === "percent"
                                ? "%"
                                : " đ"}
                            </Badge>
                          ) : null}
                          {data.orders[0].fee_other?.value ? (
                            <Badge
                              color="secondary"
                              style={{ fontSize: "0.7rem" }}
                            >
                              <IntlMessages
                                id={getFeeOtherLabelMessageId(
                                  data.orders[0].fee_other,
                                )}
                              />:{" "}
                              {formatAdjustmentValue(data.orders[0].fee_other)}
                              {data.orders[0].fee_other.type === "percent"
                                ? "%"
                                : " đ"}
                            </Badge>
                          ) : null}
                        </div>
                      )}
                      <div
                        className="d-flex justify-content-between align-items-center"
                        style={{ fontSize: "0.9rem" }}
                      >
                        <span className="text-muted">Tổng cộng</span>
                        <span
                          className="font-weight-bold text-primary"
                          style={{ fontSize: "1rem" }}
                        >
                          {AddCommaNumber(calcTotal(data?.orders))} đ
                        </span>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </PerfectScrollbar>
          ) : (
            <Card>
              <CardBody className="text-center">
                <p className="text-muted mb-0">
                  <IntlMessages id="home.provisional.empty" />
                </p>
              </CardBody>
            </Card>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};
export default ProvisionalInvoice;
